// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SearchFieldWrapper from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps, CheckboxStyle } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Select from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SelectInline from "@store/Library/Components/Toolkit/SelectInline";
import constants from "@store/constants";
import hasSameParams from "@store/Framework/Common/Utilities/Blog/hasSameParams";
import { getBlogListFilters } from "@store/Framework/Store/Store/Pages/BlogList/BlogListSelector";
import { GetBlogApiParameter } from "@store/Framework/Store/Store/Pages/BlogList/Handlers/GetBlogList";
import updateLoadParameter from "@store/Framework/Store/Store/Pages/BlogList/Handlers/UpdateLoadParameter";
import { buttonStyles } from "@store/Themes/common/mixins/button";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpointUtility from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

export interface BlogListFiltersStyles {
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    dropdownGridItem?: GridItemProps;
    gridContainerFilters?: GridContainerProps;
    gridItem?: GridItemProps;
    btnsWrapper?: GridItemProps;
    resetButton?: ButtonPresentationProps;
    countSortByWrapper?: InjectableCss;
    showArchivesCheckbox?: CheckboxProps;
    showArchivesTypography?: TypographyPresentationProps;
}

const blogListFiltersStyles: BlogListFiltersStyles = {
    wrapper: {
        css: css`
            display: flex;
            ${breakpointValues(["wrap", null, null, "nowrap"], ["flex-wrap"])}
        `,
    },
    gridContainer: {},
    gridContainerFilters: {},
    gridItem: {
        width: [12, 12, 6, 6, 6],
        align: "bottom",
        css: css`
            & + ${GridItemStyle} {
                ${CheckboxStyle} {
                    ${breakpointUtility("md", "up")} {
                        padding-left: 30px;
                        border-left: 1px solid ${getColor("common.border")};
                    }
                }
            }
        `,
    },
    dropdownGridItem: {
        width: [12, 12, 12, 7, 8],
    },
    btnsWrapper: {
        width: [12, 12, 12, 5, 4],
        css: css`
            ${breakpointValues(["center", null, null, "flex-end"], ["align-items"])}
            ${breakpointValues(["center", null, null, "flex-end"], ["justify-content"])}
        `,
    },
    resetButton: {
        variant: "primary",
        css: css`
            ${buttonStyles("solid")}
            ${breakpointValues(["100%", null, "auto"], ["width"])}
        `,
    },
    countSortByWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: ${pxToRem(20)};
        `,
    },
    showArchivesCheckbox: {
        variant: "toggle",
        css: css`
            justify-content: flex-start;
            flex-basis: content;
            gap: ${pxToRem(15)};
            ${breakpointValues([pxToRem(5, 0), null, pxToRem(10, 0), null], ["padding"])}
        `,
    },
    showArchivesTypography: {
        size: 16,
    },
};

const styles = blogListFiltersStyles;

const BlogListFilters: React.FunctionComponent = () => {
    const blogFilters = useAppSelector(state => getBlogListFilters(state));
    const initialParameter: GetBlogApiParameter = { sortBy: "Newest", isArchived: false };
    const dispatch = useDispatch();
    const [expandAccordion, setExpandAccordion] = useState(false);
    const [apiParameter, setApiParameter] = useState<GetBlogApiParameter>({ ...initialParameter });
    const [searchParameter, setSearchParameter] = useState<GetBlogApiParameter>({ ...initialParameter });
    const blogListDataView = usePersistentDataView(
        useAppSelector(state => getDataView(state.data.pages, apiParameter)),
    );
    const { isLoading: baseIsLoading, value: baseBlogList } = usePersistentDataView(
        useAppSelector(state => getDataView(state.data.pages, initialParameter)),
    );
    const { isLoading: dynamicIsLoading } = blogListDataView;
    const isLoading = baseIsLoading && dynamicIsLoading;

    useEffect(() => {
        setSearchParameter(apiParameter);
    }, []);

    useEffect(() => {
        if (searchParameter !== undefined) {
            dispatch(updateLoadParameter(searchParameter));
        }
    }, [searchParameter]);

    useEffect(() => {
        searchFiltersHandler();
    }, [apiParameter]);

    const categoryChangeHandler = (value: string) => {
        const parameters = { ...apiParameter, category: value };
        setApiParameter(parameters);
    };

    const showArchivesChangeHandler = () => {
        const parameters = { ...apiParameter, isArchived: !apiParameter.isArchived };
        setApiParameter(parameters);
    };

    const searchFiltersHandler = () => {
        if (!hasSameParams(apiParameter, searchParameter)) {
            setSearchParameter(apiParameter);
        }
    };

    const sortByChangeHandler = (value: string) => {
        const parameters = { ...apiParameter, sortBy: value };
        setApiParameter(parameters);
    };

    const resetFilters = () => {
        setApiParameter(initialParameter);
    };

    const totalPostCount = blogListDataView?.value?.length;
    const postsText = totalPostCount === 1 ? translate("Post") : translate("Posts");

    const showCategories = blogFilters?.categories && blogFilters.categories?.length > 0;
    const showArchive = blogFilters?.hasArchive;

    const renderFilters = () => (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.dropdownGridItem}>
                    <GridContainer {...styles.gridContainerFilters}>
                        {showCategories && (
                            <GridItem {...styles.gridItem}>
                                <Select
                                    label={translate("Categories")}
                                    onChange={e => categoryChangeHandler(e.currentTarget.value)}
                                    id="selectCategory"
                                    value={apiParameter.category || ""}
                                >
                                    <option value="">{translate("Categories")}</option>
                                    {blogFilters.categories?.map(option => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {showArchive && (
                            <GridItem {...styles.gridItem}>
                                <Checkbox
                                    {...styles.showArchivesCheckbox}
                                    checked={apiParameter.isArchived || false}
                                    onChange={showArchivesChangeHandler}
                                >
                                    <Typography {...styles.showArchivesTypography}>
                                        {translate("Show archives")}
                                    </Typography>
                                </Checkbox>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.btnsWrapper}>
                    <Button
                        {...styles.resetButton}
                        disabled={isLoading || !apiParameter.sortBy || hasSameParams(apiParameter, initialParameter)}
                        onClick={resetFilters}
                    >
                        {translate("Reset")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );

    return (
        <SearchFieldWrapper>
            {(showCategories || showArchive) && (
                <>
                    <Hidden above="md">
                        <Accordion headingLevel={6}>
                            <AccordionSection
                                title={translate("Filters")}
                                expanded={expandAccordion}
                                onTogglePanel={() => setExpandAccordion(!expandAccordion)}
                            >
                                {renderFilters()}
                            </AccordionSection>
                        </Accordion>
                    </Hidden>
                    <Hidden below="lg">{renderFilters()}</Hidden>
                </>
            )}
            <StyledWrapper {...styles.countSortByWrapper}>
                <StyledWrapper>
                    <Typography>{`${totalPostCount || 0} ${postsText}`}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <SelectInline
                        label={`${translate("Sort by")}${translate(":")}`}
                        labelPosition="left"
                        uid="blogListSortSelectDesktop"
                        value={apiParameter.sortBy || "Newest"}
                        onChange={event => sortByChangeHandler(event.currentTarget.value)}
                        disabled={isLoading || !apiParameter.sortBy}
                    >
                        <option value="Newest">{translate("Newest")}</option>
                        <option value="Oldest">{translate("Oldest")}</option>
                    </SelectInline>
                </StyledWrapper>
            </StyledWrapper>
        </SearchFieldWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BlogListFilters,
    definition: {
        group: constants.widgetGroup.blogList,
        displayName: "Blog List Filters",
        allowedContexts: ["BlogListPage"] as any,
    },
};

export default widgetModule;
